<script setup lang="ts">
/**
 * This component is copied and adapted from ilt_frontends/apps/manabua/src/components/ShareDialog.vue
 * Since we don't know yet how the sharing options for Quizters will look in the final version, I decided not to refactor anything into a shared component just yet.
 * We might end up using the ShareDialog in shared package components later, when we want to be able to add members to a Quizter.
 */
import { computed, onMounted, ref } from 'vue';
import { SharingOptions } from '@ilteducation/components';
import { DialogCloseButton } from '@ilteducation/components';
import type { Quizter } from '@ilteducation/quizter-types';
import useLocale from '../../../composables/use-locale';
import { useQuizterStore } from '../../stores/quizterStore';

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const props = defineProps<{
  itemToShare: Quizter;
  title: string;
}>();

const { t } = useLocale();
const quizterStore = useQuizterStore();

const showDialog = ref(true);

const shareId = ref<string | null>(null);
const isChecking = ref(true);
const checkError = ref(false);
const isSharing = ref(false);
const shareError = ref(false);
const isUnsharing = ref(false);
const unshareError = ref(false);
const showUnshareSuccess = ref(false);

const shareUrl = computed(() => (shareId.value ? `${window.location.origin}/quizter/shared/${shareId.value}` : ''));

const checkShareStatus = async () => {
  isChecking.value = true;
  checkError.value = false;
  try {
    shareId.value = await quizterStore.checkQuizterShare(props.itemToShare.activityId);
  } catch {
    checkError.value = true;
  } finally {
    isChecking.value = false;
  }
};

const share = async () => {
  isSharing.value = true;
  shareError.value = false;
  try {
    shareId.value = await quizterStore.shareQuizter(props.itemToShare.activityId);
  } catch {
    shareError.value = true;
  } finally {
    isSharing.value = false;
  }
};

const unshare = async () => {
  if (!shareId.value) {
    return;
  }
  isUnsharing.value = true;
  unshareError.value = false;
  try {
    await quizterStore.unshareQuizter(shareId.value);
    shareId.value = null;
    showUnshareSuccess.value = true;
  } catch {
    unshareError.value = true;
  } finally {
    isUnsharing.value = false;
  }
};

onMounted(checkShareStatus);

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <v-dialog
    v-model="showDialog"
    transition="scale-transition"
    scrollable
    :max-width="600"
    :max-height="700"
    class="rounded-lg"
    @keydown.esc="handleClose"
    @click:outside="handleClose"
  >
    <v-card>
      <v-card-title class="py-6 ma-0 pb-0">
        <h2 class="text-center ma-0">{{ title }}</h2>
        <DialogCloseButton @click="handleClose" />
      </v-card-title>
      <v-card-text>
        <!-- Checking whether the Quizter is already shared -->
        <div v-if="isChecking" class="d-flex justify-center py-8">
          <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
        </div>

        <!-- Failed to check the share status -->
        <div v-else-if="checkError" class="d-flex flex-column align-center ga-4 py-8">
          <p class="text-body-1 text-center ma-0">{{ t('my_quizters.share_check_error') }}</p>
          <v-btn variant="outlined" color="primary" class="text-none" @click="checkShareStatus">
            {{ t('my_quizters.retry') }}
          </v-btn>
        </div>

        <!-- Shared: show the link and let the user stop sharing -->
        <div v-else-if="shareId" class="d-flex flex-column ga-4">
          <SharingOptions
            :show-title="false"
            :sharing-items="[]"
            :isAvailableToShare="true"
            :entity-url="shareUrl"
            :with-default-options="false"
            :entity-title="itemToShare.name"
            :share-entity-url="shareUrl"
            content="video"
          >
          </SharingOptions>

          <div class="d-flex flex-column align-center ga-2">
            <v-btn variant="outlined" color="error" class="text-none" :loading="isUnsharing" @click="unshare">
              {{ t('my_quizters.unshare') }}
            </v-btn>
            <p v-if="unshareError" class="text-error text-body-2 text-center ma-0">
              {{ t('my_quizters.unshare_error') }}
            </p>
          </div>
        </div>

        <!-- Not shared yet: explain and let the user share -->
        <div v-else class="d-flex flex-column align-center ga-4 py-4">
          <p class="text-body-1 text-center ma-0">{{ t('my_quizters.share_public_info') }}</p>
          <v-btn color="primary" variant="flat" class="text-none" :loading="isSharing" @click="share">
            {{ t('my_quizters.share_button') }}
          </v-btn>
          <p v-if="shareError" class="text-error text-body-2 text-center ma-0">
            {{ t('my_quizters.share_link_error') }}
          </p>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>

  <v-snackbar v-model="showUnshareSuccess" color="success" location="bottom">
    {{ t('my_quizters.unshare_success') }}
  </v-snackbar>
</template>
